// Pure helper: flatten a cost-model node list + edge list into an ordered, depth-annotated
// list suitable for an indented dropdown. The cost model is a single-parent tree; nodes carry
// a sort_order, edges are { parent_node_id, child_node_id } (parent_node_id null = a root).
// No React, no network.

// Siblings sort by sort_order (nulls last), then name — matching the hierarchy manager's order.
const bySortThenName = (a, b) => {
  const sa = a.sort_order == null ? Number.POSITIVE_INFINITY : a.sort_order;
  const sb = b.sort_order == null ? Number.POSITIVE_INFINITY : b.sort_order;
  if (sa !== sb) return sa - sb;
  return String(a.name || "").localeCompare(String(b.name || ""));
};

// buildOwnerTreeOptions returns [{ value, label, depth, isParent }] in depth-first tree order,
// with the catch-all node removed. depth drives indentation; isParent flags nodes that have
// children so the UI can visually distinguish branches from leaves. Defensive against missing
// parents (treated as roots) and cycles (each node emitted at most once).
export function buildOwnerTreeOptions(nodes, edges, catchAllCode) {
  // Exclude the catch-all only when a code is actually given. Comparing against an undefined
  // catchAllCode would drop every node that has no `code` (undefined !== undefined is false).
  const all = (nodes || []).filter((n) => n && (catchAllCode == null || n.code !== catchAllCode));
  const byId = new Map(all.map((n) => [n.node_id, n]));

  // parent_node_id per child, and children per parent (only for parents we actually have).
  // The edges endpoint returns the json-tagless domain struct, so it serialises PascalCase
  // (ParentNodeID/ChildNodeID); accept both shapes, exactly as HierarchyManager does.
  const parentOf = new Map();
  const childrenByParent = new Map();
  for (const e of edges || []) {
    if (!e) continue;
    const childId = e.child_node_id ?? e.ChildNodeID;
    const parentId = e.parent_node_id ?? e.ParentNodeID ?? null;
    if (childId == null) continue;
    parentOf.set(childId, parentId);
    if (parentId != null && byId.has(parentId)) {
      const list = childrenByParent.get(parentId) || [];
      list.push(childId);
      childrenByParent.set(parentId, list);
    }
  }

  // Roots: nodes with no known parent in the current node set.
  const isRoot = (n) => {
    const p = parentOf.get(n.node_id);
    return p == null || !byId.has(p);
  };
  const roots = all.filter(isRoot).sort(bySortThenName);

  const childNodesOf = (id) =>
    (childrenByParent.get(id) || [])
      .map((cid) => byId.get(cid))
      .filter(Boolean)
      .sort(bySortThenName);

  const out = [];
  const seen = new Set();
  const walk = (node, depth) => {
    if (seen.has(node.node_id)) return; // cycle / multi-parent guard
    seen.add(node.node_id);
    const children = childNodesOf(node.node_id);
    out.push({ value: node.node_id, label: node.name, depth, isParent: children.length > 0 });
    for (const child of children) walk(child, depth + 1);
  };
  for (const r of roots) walk(r, 0);

  // Any node not reached (e.g. orphaned by a cycle) is appended as a depth-0 entry so it can
  // never silently vanish from the picker.
  for (const n of all) {
    if (!seen.has(n.node_id)) {
      seen.add(n.node_id);
      out.push({ value: n.node_id, label: n.name, depth: 0, isParent: false });
    }
  }
  return out;
}
